import React, { useEffect, useState } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  DialogActions,
  Container,
  Snackbar,
  Stack,
  Card,
  Chip,
  Grid,
  LinearProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { green, red } from "@mui/material/colors";
import axios from "axios";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const S3_URL = process.env.REACT_APP_S3_URL;

const SalesDisplay = ({ data }) => {
  if (!data || data.length === 0) return <Typography>Nenhuma informação disponível</Typography>;

  return (
    <Box>
      {data.map((entry) => (
        <Box key={entry.date} mb={4}>

          {entry.sellers.map((sellerEntry) => {
            const { goals, sales } = sellerEntry;
            const hasGoals = goals && goals.length > 0;

            const totalValue = sales?.reduce((sum, s) => sum + s.value, 0) || 0;
            const totalQty = sales?.reduce((sum, s) => sum + s.quantity, 0) || 0;

            return (
              <>
                <Grid container spacing={2} direction="column">
                  <Grid container spacing={1} mt={1}>
                    
                      {[
                        { label: "Vendas", value: new Intl.NumberFormat("pt-BR").format(sales.length) },
                        {
                          label: "Total Vendido",
                          value: new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(totalValue),
                        },
                        { label: "Qtd. Itens", value: totalQty },
                        {
                          label: "Ticket Médio",
                          value: new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(totalValue / sales.length || 0),
                        },
                        {
                          label: "Média/Item",
                          value: new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(totalValue / totalQty || 0),
                        },
                        {
                          label: "Maior venda",
                          value: new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(Math.max(...sales.map((s) => s.value), 0)),
                        },
                      ].map((item, index) => (
                        <Grid item xs={6} sm={4} md={4} key={index}>
                          <Card
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              width: 80,
                              height: 40,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <Typography variant="caption" color="textSecondary">
                              {item.label}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {item.value}
                            </Typography>
                          </Card>
                        </Grid>
                      ))}



                  </Grid>

                  <Typography variant="subtitle1">
                    Metas:
                  </Typography>

                  {/* Metas (se houver) */}
                  {hasGoals &&
                    goals
                      .sort((a, b) => a.target - b.target)
                      .map((goal, idx) => {
                        const isValidTarget = goal.target !== null && !isNaN(goal.target);
                        const reached = isValidTarget ? totalValue : 0;
                        const isReached = isValidTarget && reached >= goal.target;

                        const progress = isValidTarget && goal.target > 0
                          ? Math.min((reached / goal.target) * 100, 100)
                          : 0;

                        const formatValue = (val) => {
                          return new Intl.NumberFormat("pt-BR", {
                            style: goal.type === "amount" ? "currency" : "decimal",
                            currency: goal.type === "amount" ? "BRL" : undefined,
                          }).format(val);
                        };

                        return (
                          <Card
                            variant="outlined"
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: isReached ? green[50] : red[50],
                                width: "100%",             // já está ok
                                maxWidth: "100%",          // adiciona limite
                                overflowX: "hidden",       // garante não quebrar layout
                                boxSizing: "border-box",   // importante para padding não somar à largura
                              }}
                          >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle1" fontWeight="bold">
                                {goal.name}
                              </Typography>
                              {isValidTarget && (
                                <Chip
                                  label={isReached ? "Meta alcançada" : "Não alcançada"}
                                  color={isReached ? "success" : "error"}
                                  icon={isReached ? <CheckCircleIcon /> : <HighlightOffIcon />}
                                  variant="outlined"
                                />
                              )}
                            </Box>

                            {isValidTarget ? (
                              <>
                                <Typography variant="body2">
                                  Atingido: {formatValue(reached)} / {formatValue(goal.target)}
                                </Typography>

                                <Box mt={1} position="relative">
                                  <LinearProgress
                                    variant="determinate"
                                    value={progress}
                                    sx={{
                                      height: 15,
                                      borderRadius: 5,
                                      border: `1px solid ${isReached ? green[200] : red[200]}`,
                                      backgroundColor: "#eee",
                                      "& .MuiLinearProgress-bar": {
                                        backgroundColor: isReached ? green[500] : red[400],
                                      },
                                    }}
                                  />
                                  <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Typography
                                      variant="caption"
                                      fontWeight="bold"
                                      color="textSecondary"
                                    >
                                      {Math.round((reached / goal.target) * 100)}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                Sem meta atribuída para você
                              </Typography>
                            )}
                          </Card>
                        );
                      })}
                </Grid>
              </>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

const GoalDialog = ({ open, onClose, selectedItem, setApiLoading }) => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const API_AUTH = {
    username: process.env.REACT_APP_API_USER,
    password: process.env.REACT_APP_API_PASSWORD,
  };
  const [pin, setPin] = useState("");
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const handleButtonClick = (value) => {
    if (pin.length < 4) {
      setPin((prev) => prev + value);
    }
  };

  const handleDelete = () => {
    setPin((prev) => prev.slice(0, -1));
  };

  const handleConfirm = () => {
    if (pin.length === 4) {
      onSubmit(selectedDate);
    }
  };

  const onSubmit = async (date) => {
    setApiLoading(true);
    try {
      const formattedDate = date.format("YYYY-MM-DD");
      const res = await axios.get(
        `${API_BASE_URL}goals/my?start_date=${formattedDate}&end_date=${formattedDate}&user_uuid=${selectedItem.uuid}&p=${pin}`,
        {
          headers: {
            c: localStorage.getItem("companyUuid"),
            Authorization: `Basic ${btoa(
              `${API_AUTH.username}:${API_AUTH.password}`
            )}`,
          },
        }
      );
      setData(res.data);
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || "Erro ao obter metas");
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    if (open === true) {
      setPin("");
      setData(null);
      setSelectedDate(dayjs());
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      {selectedItem && (
        <div className="card">
          <div className="avatar">
            {selectedItem.avatar_url && selectedItem.avatar_url !== "" ? (
              <img
                src={`${S3_URL}/img/user/${selectedItem.avatar_url}`}
                alt={selectedItem.name}
                className="avatar-img"
              />
            ) : (
              <div className="avatar-unavailable">👤</div>
            )}
          </div>
          <div className="info">
            <span style={{ marginLeft: "10px" }}>{selectedItem.name}</span>
          </div>
        </div>
      )}
      <DialogContent
        sx={{
          overflowX: "hidden", // Evita scroll horizontal
          maxWidth: "100%",    // Garante que o conteúdo interno se encaixe
          px: 2,                // Padding horizontal padrão
        }}
      >
        
        {data != null && (
          <>
            <Box mb={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Data da busca"
                  value={selectedDate}
                  onChange={(newValue) => {
                    setSelectedDate(newValue);
                    onSubmit(newValue);
                  }}
                  format="DD/MM/YYYY"
                />
              </LocalizationProvider>
            </Box>
            <SalesDisplay data={data} />
          </>
        )}
        {data == null && (
          <>
            <DialogTitle sx={{ textAlign: "center" }}>Digite sua senha</DialogTitle>
            <Box display="flex" justifyContent="center" mb={2}>
              <Typography variant="h5">
                {"•".repeat(pin.length).padEnd(4, "○")}
              </Typography>
            </Box>
            <Container>
              <Stack spacing={1} alignItems="center">
                {[
                  [1, 2, 3],
                  [4, 5, 6],
                  [7, 8, 9],
                  ["", 0, "⌫"],
                ].map((row, rowIndex) => (
                  <Stack
                    key={rowIndex}
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                  >
                    {row.map((item, index) =>
                      item !== "" ? (
                        <Button
                          key={index}
                          variant="contained"
                          onClick={() =>
                            item === "⌫" ? handleDelete() : handleButtonClick(item)
                          }
                          sx={{
                            width: 60,
                            height: 60,
                            backgroundColor: "#8093a0",
                            color: "white",
                            fontSize: "20px",
                          }}
                        >
                          {item}
                        </Button>
                      ) : (
                        <Box key={index} sx={{ width: 60, height: 60 }} />
                      )
                    )}
                  </Stack>
                ))}
              </Stack>
            </Container>
          </>
        )}
        <Snackbar
          open={errorMessage !== ""}
          autoHideDuration={6000}
          onClose={() => {
            setErrorMessage("");
          }}
        >
          <Alert
            onClose={() => {
              setErrorMessage("");
            }}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color={data == null ? 'secondary' : 'primary'}>
          {data == null ? 'Cancelar' : 'Fechar'}
        </Button>
        {data == null && (
        <Button onClick={handleConfirm} color="primary" disabled={pin.length !== 4}>
          Confirmar
        </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GoalDialog;
